import { useEffect, useRef } from "react";
import SVG from "react-inlinesvg";
import { mutate } from "swr";
import Project from "../../models/Project";
import useUserMetadata from "../../hooks/useUserMetadata";
import { useSudowriteStore } from "../../stores/sudowriteStore";
import { CONTEXT_OVERFLOW_MESSAGE, MAX_TRIAL_NOVEL_IMPORTS } from "../../../../src/shared/constants";
import { useToast } from "../ToastWrapper";
import { useScenesMigrationStore } from "../../stores/scenesMigrationStore";
import { useCommentStore } from "../../stores/commentStore";
import { useProjectStore } from "../../stores/projectStore";
import { useWebSocket } from "./WebSocketProvider";
import { useHistory } from "react-router-dom";
import styled, { keyframes } from "styled-components/macro";
import { SudoButton } from "../../sudokit/components/SudoButton";
import { sendAppEvent } from "../../Utils";
import useImportSpeedPerceptionExperiment, { isImportSpeedPerceptionSession } from "../../hooks/useImportSpeedPerceptionExperiment";
import { isHomePath } from "../../utils/isHomePath";

const truncateTitle = (title, maxLength = 30) => {
  if (!title) return "";
  return title.length > maxLength ? `${title.substring(0, maxLength)}...` : title;
};

const myVoiceTruncateLength = 20;

// True when the pathname is the project's root route or any of its sub-routes (canvas, reviser, etc.)
const isOnProjectPath = (pathname, projectId) => pathname === `/app/project/${projectId}` || pathname.startsWith(`/app/project/${projectId}/`);

const shimmer = keyframes`
  0% {
    background-position: 0% 50%;
  }
  100% {
    background-position: 100% 50%;
  }
`;

const ToastActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ToastText = styled.span`
  flex-direction: column;
  font-size: 16px;
  font-weight: 500;

  strong {
    font-weight: 600;
  }
`;

/**
 * This component registers some of the message handlers that were used in the old socket host
 */

export function SecretWebSocketHelper() {
  const { refetch: refetchUserMetadata } = useUserMetadata();
  const subscriptionJSON = useSudowriteStore((state) => state.subscriptionJSON);
  const setIsImportDisabled = useSudowriteStore((state) => state.setIsImportDisabled);
  const editorOptions = useSudowriteStore((state) => state.editorOptions);
  const setEditorOptions = useSudowriteStore((state) => state.setEditorOptions);
  const { setToast, dismissToast } = useToast();
  const addStep = useScenesMigrationStore((state) => state.addStep);
  const setActive = useScenesMigrationStore((state) => state.setActive);
  const getCommentStore = useCommentStore;
  const { registerHandler, unregisterHandler } = useWebSocket();
  const history = useHistory();
  const autoNavigateTimeoutRef = useRef(null);
  const historyRef = useRef(history);
  const pendingProjectIdRef = useRef(null);
  const pendingProjectTitleRef = useRef(null);
  const userData = useSudowriteStore((state) => state.userData);
  const importSpeedPerceptionExperiment = useImportSpeedPerceptionExperiment();

  const disableWelcomeCard = () => {
    if (!subscriptionJSON.data.features.showWelcomeCard || !userData) return;
    const disabledPromoCards = userData.data?.disabledPromoCards || [];
    if (disabledPromoCards.includes("onboarding-card")) return;
    userData.update({ disabledPromoCards: [...disabledPromoCards, "onboarding-card"] });
  };

  useEffect(() => {
    historyRef.current = history;
  }, [history]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      // If tab becomes visible and we have a pending project to navigate to
      if (!window.document.hidden && pendingProjectIdRef.current) {
        const projectId = pendingProjectIdRef.current;
        const title = pendingProjectTitleRef.current || "Untitled Project";
        const livePath = window.location.pathname;
        pendingProjectIdRef.current = null;
        pendingProjectTitleRef.current = null;
        if (isOnProjectPath(livePath, projectId)) {
          // Already inside the imported project — just confirm completion, don't reload
          setToast(
            <ToastActions>
              <SVG src="/assets/check-simple.svg" className="success" />
              <ToastText>
                Import of <strong>"{title}"</strong> complete.
              </ToastText>
            </ToastActions>,
            { dismissible: true, dismissButtonVariant: "inverted" }
          );
        } else if (isHomePath(livePath)) {
          window.location.assign(`/app/project/${projectId}`);
        } else {
          // User left homepage while tab was hidden — don't redirect, show completion toast
          setToast(
            <ToastActions>
              <SVG src="/assets/check-simple.svg" className="success" />
              <ToastText>
                Import of <strong>"{title}"</strong> complete.
              </ToastText>
              <SudoButton
                style={{ marginLeft: "10px" }}
                variant="invertedColorSecondary"
                size="default"
                onClick={() => { dismissToast(); navigateToProject(projectId); }}
              >
                Open
              </SudoButton>
            </ToastActions>,
            { dismissible: true, dismissButtonVariant: "inverted" }
          );
        }
        if (autoNavigateTimeoutRef.current) {
          clearTimeout(autoNavigateTimeoutRef.current);
          autoNavigateTimeoutRef.current = null;
        }
      }
    };

    window.document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (autoNavigateTimeoutRef.current) {
        clearTimeout(autoNavigateTimeoutRef.current);
        autoNavigateTimeoutRef.current = null;
      }
    };
  }, []);

  const updateImportButton = async () => {
    if (subscriptionJSON.activeSubscription) return;

    const { novelImports } = await refetchUserMetadata();
    if (novelImports >= MAX_TRIAL_NOVEL_IMPORTS) {
      setIsImportDisabled(true);
    }
  };

  // More message handlers may be added through the registerHandler function
  // like BillingToast and ShareLinkPage
  const navigateToProject = (projectId) => {
    if (!projectId) return;
    if (autoNavigateTimeoutRef.current) {
      clearTimeout(autoNavigateTimeoutRef.current);
      autoNavigateTimeoutRef.current = null;
    }
    // Use window.location.assign for more reliable navigation from websocket handler
    window.location.assign(`/app/project/${projectId}`);
  };

  const messageHandlerMap = {
    projectTitleCleaned: async (msg) => {
      const { projectId, oldTitle, newTitle } = msg.payload;

      // Trigger the animation
      useProjectStore.getState().startTitleAnimation(projectId, oldTitle, newTitle);

      // Also invalidate My Voice samples cache in case this project had My Voice training
      // (the sample name gets updated to match the cleaned title)
      await mutate("/api/my_voice/style_samples");
    },
    importSuccess: async (msg) => {
      const project = await Project.get(msg.payload); // payload is id
      sendAppEvent(project?.importSource === "scrivener" ? "import_scrivener_completed" : "import_novel_completed", { projectId: msg.payload });
      await updateImportButton(); // disable import button if trial user reaches import limit

      // Invalidate My Voice samples cache to show newly created samples
      await mutate("/api/my_voice/style_samples");

      // Don't set writeModel here - it will be set when the user opens the project
      // This ensures the My Voice onboarding tooltip appears at the right time

      const truncatedTitle = truncateTitle(project?.title ?? "Untitled Project", myVoiceTruncateLength);
      const projectId = msg.payload;

      // Only auto-navigate in the tab that initiated the import.
      // sessionStorage is per-tab, so only the originating tab will have the matching ID.
      const isOriginatingTab = sessionStorage.getItem("pendingImportProjectId") === projectId;
      if (isOriginatingTab) {
        sessionStorage.removeItem("pendingImportProjectId");
      }

      const handleManualNavigate = () => {
        dismissToast();
        navigateToProject(projectId);
      };

      // Only show toast and auto-navigate in the tab that initiated the import
      if (!isOriginatingTab) return;

      // If the user is already inside this project (the new fast-path flow redirects into
      // the project as soon as the document is uploaded), skip the "Opening project" redirect
      // toast entirely and show a lightweight success confirmation instead. The in-editor
      // ImportProgressBanner will disappear as soon as Firestore clears `importStep`.
      const currentPath = historyRef.current?.location?.pathname || window.location.pathname;
      const alreadyOnProject = isOnProjectPath(currentPath, projectId);
      const treatmentImportSession = isImportSpeedPerceptionSession(projectId) || importSpeedPerceptionExperiment;

      if (alreadyOnProject && treatmentImportSession) {
        setToast(
          <ToastActions>
            <SVG
              src="/assets/check-simple.svg"
              className="success"
            />
            <ToastText>
              Import of <strong>"{truncatedTitle}"</strong> complete. Your Story Bible is ready.
            </ToastText>
          </ToastActions>,
          { dismissible: true, dismissButtonVariant: "inverted" }
        );
        return;
      }

      // If user has left the homepage, don't auto-navigate — show a static toast instead
      const isStillOnHome = isHomePath(currentPath);
      if (!isStillOnHome && !alreadyOnProject) {
        setToast(
          <ToastActions>
            <SVG src="/assets/check-simple.svg" className="success" />
            <ToastText>
              Import of <strong>"{truncatedTitle}"</strong> complete.
            </ToastText>
            <SudoButton
              style={{ marginLeft: "10px" }}
              variant="invertedColorSecondary"
              size="default"
              onClick={handleManualNavigate}
            >
              Open
            </SudoButton>
          </ToastActions>,
          { dismissible: true, dismissButtonVariant: "inverted" }
        );
        return;
      }

      setToast(
        <ToastActions>
          <SVG
            src="/assets/check-simple.svg"
            className="success"
          />
          <ToastText>
            Import of <strong>"{truncatedTitle}"</strong> Complete. Opening project
          </ToastText>
          <SudoButton
            style={{ marginLeft: "10px" }}
            state="loading"
            variant="invertedColorSecondary"
            size="default"
            loadingPercentage={90}
            loadingTransitionDuration={5}
            onClick={handleManualNavigate}
          >
            Go
          </SudoButton>
        </ToastActions>,
        { dismissible: true, dismissButtonVariant: "inverted" }
      );

      pendingProjectIdRef.current = projectId;
      pendingProjectTitleRef.current = truncatedTitle;

      autoNavigateTimeoutRef.current = setTimeout(() => {
        // Only navigate if tab is visible
        if (!window.document.hidden) {
          const livePath = window.location.pathname;
          if (isOnProjectPath(livePath, projectId)) {
            // User already entered the imported project during the wait — just confirm completion
            pendingProjectIdRef.current = null;
            pendingProjectTitleRef.current = null;
            setToast(
              <ToastActions>
                <SVG src="/assets/check-simple.svg" className="success" />
                <ToastText>
                  Import of <strong>"{truncatedTitle}"</strong> complete.
                </ToastText>
              </ToastActions>,
              { dismissible: true, dismissButtonVariant: "inverted" }
            );
          } else if (isHomePath(livePath)) {
            pendingProjectIdRef.current = null;
            pendingProjectTitleRef.current = null;
            navigateToProject(projectId);
          } else {
            // User navigated away during the wait — cancel auto-navigate
            pendingProjectIdRef.current = null;
            pendingProjectTitleRef.current = null;
            setToast(
              <ToastActions>
                <SVG src="/assets/check-simple.svg" className="success" />
                <ToastText>
                  Import of <strong>"{truncatedTitle}"</strong> complete.
                </ToastText>
                <SudoButton
                  style={{ marginLeft: "10px" }}
                  variant="invertedColorSecondary"
                  size="default"
                  onClick={() => { dismissToast(); navigateToProject(projectId); }}
                >
                  Open
                </SudoButton>
              </ToastActions>,
              { dismissible: true, dismissButtonVariant: "inverted" }
            );
          }
        }
        // If tab is hidden, navigation will happen when user returns to tab
      }, 5000);
    },
    importFailure: async (msg) => {
      // Payload may be a string (legacy title), or { title, reason?, projectId?, importSource?, preservedProject? }
      const isScrivener = typeof msg.payload === "object" && msg.payload?.importSource === "scrivener";
      const failureProjectId = typeof msg.payload === "object" && msg.payload?.projectId ? msg.payload.projectId : null;
      const payloadObj = typeof msg.payload === "object" && msg.payload != null ? msg.payload : null;
      const preservedProject = payloadObj?.preservedProject === true;
      const isOriginatingTab = failureProjectId && sessionStorage.getItem("pendingImportProjectId") === failureProjectId;
      const currentPath = historyRef.current?.location?.pathname || window.location.pathname;
      const alreadyOnProject = failureProjectId && isOnProjectPath(currentPath, failureProjectId);
      const failureEvent = new CustomEvent("sudowrite:import-failure", {
        detail: { projectId: failureProjectId },
        cancelable: true,
      });
      if (failureProjectId) {
        window.dispatchEvent(failureEvent);
      }
      sendAppEvent(isScrivener ? "import_scrivener_failed" : "import_novel_failed", {
        error: typeof msg.payload === "object" ? msg.payload?.reason || "unknown" : "unknown",
        importType: isScrivener ? "scrivener" : "document",
        source: "worker",
      });

      if (preservedProject && isOriginatingTab) {
        sessionStorage.removeItem("pendingImportProjectId");
      }
      // Suppress the fallback only when an active report waiter handles this failure.
      if (preservedProject && isOriginatingTab && !alreadyOnProject && failureEvent.defaultPrevented) return;

      let toastMessage;
      if (payloadObj?.reason === CONTEXT_OVERFLOW_MESSAGE) {
        toastMessage = `"${truncateTitle(payloadObj.title)}" exceeds the length limit for the AI. Please try uploading a shorter file.`;
      } else if (preservedProject) {
        toastMessage = `"${truncateTitle(payloadObj?.title)}" couldn't finish importing. Your project is still available — open it to review and try again.`;
      } else {
        const titleForToast = typeof msg.payload === "object" ? msg.payload?.title : msg.payload;
        toastMessage = `"${truncateTitle(titleForToast)}" failed to import. Please try again.`;
      }
      setToast(
        <>
          <SVG
            src="/assets/close.svg"
            className="failed"
          />
          {toastMessage}
        </>
      );
    },
    importCancelled: (msg) => {
      // same as above, project is deleted so we receive the title as payload
      setToast(<>Import cancelled for "{truncateTitle(msg.payload)}"</>);
    },
    myVoiceTrainingFailure: (msg) => {
      // Show warning toast that My Voice training failed but import continues
      const errorMessage = msg.payload?.errorMessage || "My Voice training failed";
      setToast(
        <>
          <SVG
            src="/assets/alert.svg"
            className="warning"
          />
          {errorMessage}. Your import will continue without My Voice training.
        </>,
        { dismissible: true }
      );
    },
    projectScenesMigrationStep: (msg) => {
      if (msg.payload.step.completed) {
        setActive(msg.payload.projectId, false);
      } else {
        addStep(msg.payload.projectId, msg.payload.step);
      }
    },
    refetchComments: (msg) => {
      // Delay to work around SWR's 2-second dedup window and ensure DB transaction completes
      setTimeout(() => {
        // Check projectStore for the active document (commentStore.documentId may not be set)
        // This check is inside setTimeout so we verify the user is still on the same document
        const currentDocument = useProjectStore.getState().document;
        if (currentDocument?.id !== msg.payload.documentId) {
          return;
        }

        const { mutateComments, isMutateCommentsReady } = getCommentStore.getState();
        const isStoreMutateReady = isMutateCommentsReady;

        if (isStoreMutateReady) {
          void mutateComments();
          return;
        }

        // Fallback: global SWR mutate supports a key matcher (cache.keys lives on Cache, not on mutate).
        // useComments keys are tuples: [documentId, userId, isAuthor].
        void mutate(
          (key) => Array.isArray(key) && key[0] === msg.payload.documentId,
          undefined,
          { revalidate: true }
        );
      }, 500);
    },
  };

  useEffect(() => {
    // Register all message handlers with the websocket
    Object.entries(messageHandlerMap).forEach(([eventType, handler]) => {
      registerHandler(eventType, handler);
    });

    // Cleanup function to remove event listeners when component unmounts
    return () => {
      Object.keys(messageHandlerMap).forEach((eventType) => {
        unregisterHandler(eventType);
      });
    };
  }, []);

  return <></>;
}
